import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Car, ChevronRight } from 'lucide-react';

const VEHICLES = [
  { value: 'luxury-sedan', label: 'First-Class Sedan', rate: 75, cat: 'Concierge Driven', minHrs: 3 },
  { value: 'mercedes-s-class', label: 'Premium Sedan', rate: 150, cat: 'Concierge Driven', minHrs: 3 },
  { value: 'escalade-esv', label: 'First-Class SUV', rate: 100, cat: 'Concierge Driven', minHrs: 3 },
  { value: 'premium-escalade', label: 'Premium SUV', rate: 125, cat: 'Concierge Driven', minHrs: 3 },
  { value: 'cadillac-escalade-esv-concierge', label: '2023 Escalade ESV', rate: 100, cat: 'Concierge Driven', minHrs: 3 },
  { value: 'sprinter-shuttle', label: 'Basic Sprinter', rate: 200, cat: 'Group Driven', minHrs: 4 },
  { value: 'sprinter-limo', label: 'Luxury Sprinter', rate: 200, cat: 'Group Driven', minHrs: 4 },
  { value: 'mini-bus', label: 'Luxury Bus (27 pax)', rate: 250, cat: 'Group Driven', minHrs: 4 },
  { value: 'executive-bus', label: 'Coach Bus (37 pax)', rate: 275, cat: 'Group Driven', minHrs: 5 },
  { value: 'bentley-flying-spur', label: 'Bentley Flying Spur', rate: 250, cat: 'Exotic Driven', minHrs: 3 },
  { value: 'lamborghini-urus', label: 'Lamborghini Urus', rate: 300, cat: 'Exotic Driven', minHrs: 3 },
  { value: 'maybach-gls600', label: 'Maybach GLS600', rate: 300, cat: 'Exotic Driven', minHrs: 3 },
  { value: 'rolls-royce-cullinan', label: 'Rolls-Royce Cullinan', rate: 450, cat: 'Exotic Driven', minHrs: 3 },
];

const ALL_HOURS = [1, 2, 3, 4, 5, 6, 8, 10, 12];

const QuickQuote = () => {
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState('');
  const [hours, setHours] = useState('3');

  const selected = VEHICLES.find(v => v.value === vehicle);
  const effectiveMinHrs = selected?.minHrs || 3;
  const validHours = ALL_HOURS.filter(h => h >= effectiveMinHrs);
  const effectiveHours = Math.max(effectiveMinHrs, parseInt(hours) || effectiveMinHrs);
  const total = selected ? selected.rate * effectiveHours : 0;

  // Auto-adjust hours when vehicle changes
  useEffect(() => {
    if (selected && parseInt(hours) < selected.minHrs) {
      setHours(String(selected.minHrs));
    }
  }, [vehicle, selected, hours]);

  const bookNow = () => {
    const params = new URLSearchParams({ vehicle, hours, service: 'hourly' });
    navigate(`/booking?${params.toString()}`);
  };

  return (
    <div className="bg-black border-t border-gray-800" data-testid="quick-quote">
      <div className="max-w-7xl mx-auto px-6 py-10 sm:py-14">
        <div className="max-w-md mx-auto sm:max-w-lg">
          {/* Title + About ACE text merged */}
          <div className="text-center mb-8">
            <p className="text-[10px] text-gray-500 font-bold tracking-[0.3em] uppercase mb-3">INSTANT ESTIMATE</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
              Your<span className="text-red-500">Auto</span>Concierge
            </h2>
            <p className="text-lg text-white font-light mt-1">Quick Quote</p>
            <div className="h-px w-16 bg-red-600 mx-auto mt-4 mb-4" />
            <p className="text-sm text-gray-400 leading-relaxed max-w-lg mx-auto">
              At Auto Concierge Enterprises, we don't just offer car service, we offer an entire experience. From personalized Concierge-Driven car service to tailored Auto Lifestyle Consultations, we'll help you elevate every aspect of your Auto Lifestyle.
            </p>
          </div>

          {/* Stacked Form */}
          <div className="space-y-4">
            <div>
              <label className="text-[10px] text-gray-500 font-bold uppercase tracking-wider mb-1.5 block">Select Vehicle</label>
              <Select value={vehicle} onValueChange={setVehicle}>
                <SelectTrigger className="h-14 bg-gray-900 border-gray-700 text-white text-sm rounded-lg" data-testid="qq-vehicle">
                  <SelectValue placeholder="Choose a vehicle..." />
                </SelectTrigger>
                <SelectContent>
                  {['Concierge Driven', 'Group Driven', 'Exotic Driven'].map(cat => {
                    const items = VEHICLES.filter(v => v.cat === cat);
                    return (
                      <React.Fragment key={cat}>
                        <div className="px-2 py-1.5 text-[10px] font-bold text-red-500 uppercase tracking-wider">{cat}</div>
                        {items.map(v => (
                          <SelectItem key={v.value} value={v.value}>
                            {v.label} — ${v.rate}/hr
                          </SelectItem>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-[10px] text-gray-500 font-bold uppercase tracking-wider mb-1.5 block">Hours</label>
              <Select value={hours} onValueChange={setHours}>
                <SelectTrigger className="h-14 bg-gray-900 border-gray-700 text-white text-sm rounded-lg" data-testid="qq-hours">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {validHours.map(h => (
                    <SelectItem key={h} value={String(h)}>{h} {h === 1 ? 'hour' : 'hours'}{h === effectiveMinHrs ? ' (min)' : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Estimate Display */}
            <div className="bg-gray-900 border border-gray-700 rounded-lg p-5 text-center">
              <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider mb-2">Estimated Total</p>
              <p className={`text-4xl font-bold tracking-tight ${total ? 'text-white' : 'text-gray-700'}`} data-testid="qq-total">
                {total ? `$${total.toLocaleString()}` : '—'}
              </p>
              {selected && (
                <p className="text-xs text-gray-500 mt-1">
                  {effectiveHours} hrs × ${selected.rate}/hr
                </p>
              )}
            </div>

            <Button
              onClick={bookNow}
              disabled={!vehicle}
              className="w-full h-14 bg-red-600 hover:bg-red-700 text-white font-semibold text-base disabled:opacity-30 rounded-lg"
              data-testid="qq-book-btn"
            >
              <Car className="h-5 w-5 mr-2" />Book Now
              <ChevronRight className="h-5 w-5 ml-2" />
            </Button>
          </div>

          <p className="text-[10px] text-gray-600 text-center mt-4">
            Taxes & gratuity not included. Members save up to 30%.
          </p>
        </div>
      </div>
    </div>
  );
};

export default QuickQuote;
